import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Phone, Mail, MapPin } from "lucide-react";
import { BRAND, SERVICE_CATEGORIES, SUB_BRANDS } from "../../data/content";
import { POLICIES } from "../../data/policies";
import { COVERAGE_AREAS } from "../../data/locationContent";
import { trackEvent } from "../../lib/analytics";
import BrandLogo from "../BrandLogo";

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.43z"/>
  </svg>
);

const GoogleIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M21.805 10.023h-9.765v3.977h5.6c-.242 1.312-1.46 3.852-5.6 3.852-3.367 0-6.117-2.789-6.117-6.227 0-3.437 2.75-6.226 6.117-6.226 1.914 0 3.203.816 3.938 1.523l2.68-2.578C16.93 2.797 14.742 1.8 12.04 1.8 6.477 1.8 2 6.273 2 11.625s4.477 9.823 10.04 9.823c5.797 0 9.617-4.07 9.617-9.805 0-.656-.07-1.156-.152-1.62z"/>
  </svg>
);

export default function Footer() {
  return (
    <footer className="bg-[#0A0F1C] text-white border-t border-white/10" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <BrandLogo variant="icon-white" className="h-16 w-auto block mb-6" />
            <p className="text-white/60 leading-relaxed max-w-sm">
              The UK's one-stop property compliance consultancy. Multi-discipline risk management — asbestos, fire, water, damp &amp; mould and building safety — delivered with audit-ready precision nationwide.
            </p>
            <div className="mt-6 space-y-3 text-sm">
              <a href={`tel:${BRAND.phone.replace(/\s/g,"")}`} onClick={() => trackEvent("contact_phone", { location: "footer" })} className="flex items-center gap-3 text-white/70 hover:text-white" data-testid="footer-phone">
                <Phone className="w-4 h-4" /> {BRAND.phone}
              </a>
              <a href={`mailto:${BRAND.email}`} onClick={() => trackEvent("contact_email", { location: "footer" })} className="flex items-center gap-3 text-white/70 hover:text-white" data-testid="footer-email">
                <Mail className="w-4 h-4" /> {BRAND.email}
              </a>
              <div className="flex items-start gap-3 text-white/70" data-testid="footer-address">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{BRAND.address.line1}, {BRAND.address.line2}, {BRAND.address.city}, {BRAND.address.region}, {BRAND.address.postcode}</span>
              </div>
            </div>
          </div>

          <div className="md:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-5">Services</p>
            <ul className="space-y-3 text-sm">
              {SERVICE_CATEGORIES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="text-white/70 hover:text-white gs-link-underline">{s.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-5">Brands</p>
            <ul className="space-y-3 text-sm">
              {Object.values(SUB_BRANDS).map((b) => (
                <li key={b.slug}>
                  <a href={b.website} target="_blank" rel="noreferrer" className="text-white/70 hover:text-white gs-link-underline">{b.name}</a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-5">Company</p>
            <ul className="space-y-3 text-sm">
              <li><Link to="/about" className="text-white/70 hover:text-white gs-link-underline">About</Link></li>
              <li><Link to="/sectors" className="text-white/70 hover:text-white gs-link-underline">Sectors</Link></li>
              <li><Link to="/projects" className="text-white/70 hover:text-white gs-link-underline">Projects</Link></li>
              <li><Link to="/compliance-hub" className="text-white/70 hover:text-white gs-link-underline">Compliance Hub</Link></li>
              <li><Link to="/blog" className="text-white/70 hover:text-white gs-link-underline">Blog</Link></li>
              <li><Link to="/coverage" className="text-white/70 hover:text-white gs-link-underline">Coverage Areas</Link></li>
              <li><Link to="/accreditations" className="text-white/70 hover:text-white gs-link-underline">Accreditations</Link></li>
              <li><Link to="/contact" className="text-white/70 hover:text-white gs-link-underline">Contact</Link></li>
              <li><Link to="/admin/login" className="text-white/40 hover:text-white/80 text-xs" data-testid="footer-admin-link">Staff Login</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-5">Follow</p>
            <div className="flex flex-wrap gap-3">
              <a href={BRAND.social.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="w-10 h-10 border border-white/15 flex items-center justify-center hover:bg-white hover:text-[#0A0F1C] transition-colors" data-testid="social-linkedin"><Linkedin className="w-4 h-4" /></a>
              <a href={BRAND.social.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="w-10 h-10 border border-white/15 flex items-center justify-center hover:bg-white hover:text-[#0A0F1C] transition-colors" data-testid="social-facebook"><Facebook className="w-4 h-4" /></a>
              <a href={BRAND.social.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="w-10 h-10 border border-white/15 flex items-center justify-center hover:bg-white hover:text-[#0A0F1C] transition-colors" data-testid="social-instagram"><Instagram className="w-4 h-4" /></a>
              <a href={BRAND.social.tiktok} target="_blank" rel="noreferrer" aria-label="TikTok" className="w-10 h-10 border border-white/15 flex items-center justify-center hover:bg-white hover:text-[#0A0F1C] transition-colors" data-testid="social-tiktok"><TikTokIcon className="w-4 h-4" /></a>
              <a href={BRAND.social.google} target="_blank" rel="noreferrer" aria-label="Google Business Profile" className="w-10 h-10 border border-white/15 flex items-center justify-center hover:bg-white hover:text-[#0A0F1C] transition-colors" data-testid="social-google"><GoogleIcon className="w-4 h-4" /></a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-12 border-t border-white/10">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-5">Policies &amp; governance</p>
          <ul className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
            {POLICIES.map((p) => {
              const customPaths = {
                "environmental-plan": "/environmental-plan",
                "terms-of-service": "/termsofservice",
              };
              const to = customPaths[p.slug] || `/policies/${p.slug}`;
              return (
                <li key={p.slug}>
                  <Link to={to} className="text-white/60 hover:text-white gs-link-underline" data-testid={`footer-policy-${p.slug}`} dangerouslySetInnerHTML={{ __html: p.shortTitle }} />
                </li>
              );
            })}
          </ul>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10" data-testid="footer-coverage">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">UK coverage</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-white/55">
            {COVERAGE_AREAS.map((c) => (
              <React.Fragment key={c.slug}>
                <Link to="/coverage" className="hover:text-white" data-testid={`footer-coverage-${c.slug}`}>{c.name}</Link>
                <span className="text-white/20">·</span>
              </React.Fragment>
            ))}
            <Link to="/contact" className="text-[#7DD3C7] font-semibold hover:text-white" data-testid="footer-coverage-uk-wide">
              UK-wide coverage →
            </Link>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs text-white/40" data-testid="footer-copyright">© {new Date().getFullYear()} Oxford Environmental Consultants Limited. Trading as OEC, Firesurv and Mouldsurv. All rights reserved.</p>
          <p className="text-xs text-white/40" data-testid="footer-registration">Registered in England &amp; Wales · Company No. 11540812 · ISO 9001 · SMAS Worksafe · SSIP · NORAC Member</p>
        </div>
      </div>
    </footer>
  );
}
